import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { restoreVersion, versions } from '../../api/history.ts';
import { downloadUrl } from '../../api/files.ts';
import { formatBytes, formatDateTime } from '../../lib/format.ts';
import { toast } from '../../store/toast.ts';
import { ConfirmDialog } from '../common/ConfirmDialog.tsx';
import type { ApiNode, VersionItem } from '../../api/types.ts';

const ORIGIN_LABEL: Record<VersionItem['origin'], string> = {
  web: '웹', desktop: 'PC', api: 'API', restore: '복원',
};

export function HistoryTab({ namespaceId, node }: { namespaceId: string; node: ApiNode }) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<VersionItem | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['versions', node.id],
    queryFn: () => versions(namespaceId, node.path),
    enabled: node.type === 'file',
  });

  const restore = useMutation({
    mutationFn: (versionId: string) => restoreVersion(namespaceId, node.path, versionId),
    onSuccess: () => {
      toast.success('이전 버전으로 복원했습니다');
      void queryClient.invalidateQueries({ queryKey: ['versions', node.id] });
      void queryClient.invalidateQueries({ queryKey: ['nodes', namespaceId] });
      setPending(null);
    },
    onError: (e: Error) => { toast.error(e); setPending(null); },
  });

  const download = async (versionId: string): Promise<void> => {
    try {
      window.location.href = await downloadUrl(namespaceId, node.path, versionId);
    } catch (e) { toast.error(e); }
  };

  if (node.type !== 'file') return <p className="muted">폴더는 버전 이력이 없습니다.</p>;
  if (isLoading) return <span className="spinner" />;
  if (error) return <p className="error-text">{(error as Error).message}</p>;

  const list = data?.versions ?? [];

  return (
    <>
      <div className="vlist">
        {list.map((version, index) => (
          <div key={version.id} className="vitem" data-current={version.is_current}>
            <div className="vitem__rail">
              <span className="vitem__dot" />
              {index < list.length - 1 && <span className="vitem__line" />}
            </div>
            <div>
              <div className="vitem__title">
                {version.is_current ? '현재 버전' : `v${version.seq}`}
                <span className="faint" style={{ fontWeight: 400 }}> · {formatBytes(version.size)}</span>
              </div>
              <div className="vitem__meta">
                {formatDateTime(version.created_at)}
                {' · '}{version.created_by?.name ?? '알 수 없음'}
                {' · '}{version.device?.name ?? ORIGIN_LABEL[version.origin]}
              </div>
              {version.note && <div className="vitem__meta faint">{version.note}</div>}
              <div className="vitem__actions">
                <button
                  className="btn btn--sm"
                  onClick={() => { void download(version.id); }}
                >
                  다운로드
                </button>
                {!version.is_current && (
                  <button className="btn btn--sm" onClick={() => { setPending(version); }}>
                    이 버전으로 복원
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="muted">아직 이력이 없습니다.</p>}
      </div>

      {pending && (
        <ConfirmDialog
          title="이전 버전으로 복원"
          message={`v${pending.seq} (${formatDateTime(pending.created_at)}) 내용으로 되돌립니다.\n현재 버전은 이력에 그대로 남습니다.`}
          confirmLabel="복원"
          busy={restore.isPending}
          onConfirm={() => { restore.mutate(pending.id); }}
          onCancel={() => { setPending(null); }}
        />
      )}
    </>
  );
}
